import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { create } from 'zustand';
import { useTranslation } from 'react-i18next';
import { ApiError, isUnitsLocked } from '@immoaktas/api-client';
import { useToast } from '../ui';
import { errorMessage } from '../i18n';

/** What the unlock dialog needs: quantity that unlocks everything, today's allowance (null = unlimited), locked units. */
export type UnlockDetails = { needed: number; allowed: number | null; locked: number };
/**
 * Unlock dialog store (docs/BILLING.md §5 "Sperren statt Blockieren"): opened by the incentive banners and by a
 * 402 UNITS_LOCKED of a gated action (finalise, send, draft PDF, tenant invite). Creating data never opens it.
 */
export const usePaywall = create<{ open: boolean; details: UnlockDetails | null; show: (d: UnlockDetails) => void; hide: () => void }>((set) => ({ open: false, details: null, show: (details) => set({ open: true, details }), hide: () => set({ open: false }) }));

/** For calls outside useAct (PDF downloads): a 402 UNITS_LOCKED opens the unlock dialog, everything else is a toast. */
export function useErrorOrUnlock() {
  const toast = useNotify();
  const paywall = usePaywall((s) => s.show);
  return (e: unknown) => (e instanceof ApiError && e.code === 'UNITS_LOCKED' && isUnitsLocked(e.details) ? paywall({ needed: e.details.needed, allowed: e.details.allowed, locked: e.details.locked }) : toast.err(e));
}

/** Mutation with toast, invalidation and paywall handling. Money/finalize are never optimistic. */
/** Toasts with translated error texts (API errors by code, never the server sentence). */
export function useNotify() {
  const toast = useToast();
  // the text itself: the toast only knows ApiError and would replace anything else by the generic message
  return { ok: toast.ok, err: (e: unknown) => toast.err(errorMessage(e)) };
}

/**
 * `undo`: where the API can take the action back, the success toast offers "Rückgängig" (docs/10: optimistic only
 * with undo). It returns the call that reverts it (or null when this result cannot be undone); the same queries are
 * refreshed afterwards.
 */
export function useAct<A, R>(fn: (a: A) => Promise<R>, opts: { ok?: string | ((r: R) => string); invalidate?: QueryKey[]; onSuccess?: (r: R, a: A) => void; undo?: (r: R, a: A) => (() => Promise<unknown>) | null } = {}) {
  const qc = useQueryClient();
  const toast = useNotify();
  const paywall = usePaywall((s) => s.show);
  const { t } = useTranslation('common');
  return useMutation({
    mutationFn: fn,
    onSuccess: async (r, a) => {
      for (const k of opts.invalidate ?? []) await qc.invalidateQueries({ queryKey: k });
      const revert = opts.undo?.(r, a) ?? null;
      const action = revert
        ? {
            label: t('ui.undo'),
            run: () =>
              void revert()
                .then(async () => {
                  for (const k of opts.invalidate ?? []) await qc.invalidateQueries({ queryKey: k });
                  toast.ok(t('ui.undone'));
                })
                .catch((e: unknown) => toast.err(e)),
          }
        : undefined;
      if (opts.ok) toast.ok(typeof opts.ok === 'function' ? opts.ok(r) : opts.ok, action);
      opts.onSuccess?.(r, a);
    },
    onError: (e) => {
      // a gated action on locked units opens the unlock dialog; other limits (AI daily cap) are a plain error message
      if (e instanceof ApiError && e.code === 'UNITS_LOCKED' && isUnitsLocked(e.details)) return paywall({ needed: e.details.needed, allowed: e.details.allowed, locked: e.details.locked });
      if (e instanceof ApiError && e.code === 'CONFLICT') return; // shown inline via ConflictNotice
      toast.err(e);
    },
  });
}

export const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' });
export const can = (role: string | null | undefined, action: 'write' | 'finalize' | 'admin' | 'upload' | 'money') =>
  !!role && ({ write: ['owner', 'editor'], finalize: ['owner', 'editor'], admin: ['owner'], upload: ['owner', 'editor', 'caretaker'], money: ['owner', 'editor'] } as const)[action].includes(role as never);
